import moment, { Moment } from "moment-timezone";

import { getAbbreviatedTimezoneName, TimezoneKey } from "./timezone";

export const DATE_FORMAT = "DD-MM-YYYY";

export function capitalize(str: string | undefined | null) {
    if (str === undefined || str === null) {
        return str;
    }
    if (str.length > 0) {
        const newStr = str.charAt(0).toUpperCase() + str.substring(1);
        return newStr;
    } else {
        return "";
    }
}

export function formatUserName(
    user: { firstname: string; lastname: string } | undefined | null
) {
    return user ? [user.firstname, user.lastname].join(" ") : "";
}

export function formatDate(date: string | Date) {
    return date ? formatMoment(moment(date)) : "";
}

export function formatMoment(moment: Moment) {
    return moment.format(DATE_FORMAT);
}

export function formatDateTime(date: string | Date) {
    return date ? moment(date).format(DATE_FORMAT + " HH:mm:ss") : "";
}

export function formatDateWithTimezone(
    date: string | Date | Moment,
    timezone: { name: string }
) {
    const tzDate = moment.tz(date, timezone.name);
    const formattedDate = tzDate.format(DATE_FORMAT);
    return formattedDate;
}

export function formatDateTimeAndTimezone(
    date: string | Date,
    timezone: { name: string }
) {
    if (date === "") {
        return "";
    }
    const tzName = timezone.name;
    const tzDate = moment.tz(date, tzName);
    const formattedDate =
        tzDate.format(DATE_FORMAT + " H:mm") +
        " " +
        getAbbreviatedTimezoneName(tzName as TimezoneKey);
    return formattedDate;
}

export function formatTimeAndTimezone(
    date: string | Date | Moment,
    timezone: { name: string }
) {
    const tzName = timezone.name;
    const tzDate = moment.tz(date, tzName);
    const formattedTime =
        tzDate.format("H:mm") +
        " " +
        getAbbreviatedTimezoneName(tzName as TimezoneKey);
    return formattedTime;
}

export function leftPad(value: string | number, size: number, pad: string) {
    value = value.toString();
    if (value.length < size) {
        size -= value.length;
        let res = "";
        for (;;) {
            if (size & 1) {
                res += pad;
            }
            size >>= 1;
            if (size) {
                pad += pad;
            } else {
                break;
            }
        }
        return res + value;
    }
    return value;
}

export function formatDateFast(date?: Date): string {
    if (date) {
        return (
            leftPad(date.getDate(), 2, "0") +
            "-" +
            leftPad(date.getMonth() + 1, 2, "0") +
            "-" +
            date.getFullYear()
        );
    } else {
        return "";
    }
}

export function formatDateTimeFast(date: Date | undefined | null) {
    if (date) {
        return (
            leftPad(date.getDate(), 2, "0") +
            "-" +
            leftPad(date.getMonth() + 1, 2, "0") +
            "-" +
            date.getFullYear() +
            " " +
            leftPad(date.getHours(), 2, "0") +
            ":" +
            leftPad(date.getMinutes(), 2, "0") +
            ":" +
            leftPad(date.getSeconds(), 2, "0")
        );
    } else {
        return "";
    }
}

export function formatDateInverseFast(date?: Date): string {
    if (date) {
        return (
            date.getFullYear() +
            "-" +
            leftPad(date.getMonth() + 1, 2, "0") +
            "-" +
            leftPad(date.getDate(), 2, "0")
        );
    } else {
        return "";
    }
}

export function formatFileSize(fileSizeInBytes: number): string {
    if (fileSizeInBytes >= 1000000000) {
        return (fileSizeInBytes / 1000000000).toFixed(2) + " GB";
    }
    if (fileSizeInBytes >= 1000000) {
        return (fileSizeInBytes / 1000000).toFixed(2) + " MB";
    }
    return (fileSizeInBytes / 1000).toFixed(2) + " KB";
}

export interface CoveredPeriodStrings {
    label: string;
    sortString: string;
}

export function jsonObjectToString(object: any): string {
    return object ? JSON.stringify(object) : "";
}

export function formatFinancialYear(financialYear: {
    startDay: number;
    startMonth: number;
}): string {
    const { startDay, startMonth } = financialYear;

    // Start date for current financial year
    const startDate = moment
        .utc()
        .date(startDay)
        .month(startMonth - 1)
        .hours(12);

    // End date for current financial year
    const endDate = moment(startDate).add(1, "years").subtract(1, "days");

    const formattedStartDate = startDate.format("DD-MM");
    const formattedEndDate = endDate.format("DD-MM");
    return `${formattedStartDate} ${formattedEndDate}`;
}

// Escapes a string to be displayed safely when rendered to markdown
// See here for the list of escaped characters :
// https://github.com/mattcone/markdown-guide/blob/master/_basic-syntax/escaping-characters.md
export function escapeMarkdownValue(input: string): string {
    return input.replace(/[\\`\*_\{\}\[\]<>\(\)#\+\-\.!\|]/g, "\\$&");
}

/**
 * Formats a filename with date and time
 *
 * Useful for exports
 *
 * @param name
 * @param ext
 */
export function formatFileNameForExport(name: string, ext: string) {
    return `${name}_${moment(Date.now()).format("YYYY-MM-DD-HHmmss")}.${ext}`;
}
